import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Input } from './ui/input';
import { Eraser, Pen, Type, Upload, Crop } from 'lucide-react';
import './SignatureCrop.css';

const SignatureCanvas = ({ onSave, onCancel, color = '#000000' }) => {
  const canvasRef = useRef(null);
  const canvasContainerRef = useRef(null);
  const cropContainerRef = useRef(null);
  const [signatureType, setSignatureType] = useState('draw');
  // isDrawingRef tracks whether a stroke is in progress.
  // Using a ref (not state) so native touch handlers always read the current value
  // without stale-closure issues, and without triggering extra re-renders.
  const isDrawingRef = useRef(false);
  const colorRef = useRef(color);
  const [typedText, setTypedText] = useState('');
  const [uploadedImage, setUploadedImage] = useState(null);
  const [selectedFont, setSelectedFont] = useState('Dancing Script');
  const [canvasSize, setCanvasSize] = useState({ width: 600, height: 200 });

  // Crop states - drag-to-resize crop box
  const [showCrop, setShowCrop] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [cropBox, setCropBox] = useState({ x: 50, y: 50, width: 300, height: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(null); // 'nw', 'ne', 'sw', 'se', 'n', 's', 'e', 'w'
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [displayInfo, setDisplayInfo] = useState({ scale: 1, offsetX: 0, offsetY: 0 });

  // Sync canvas internal resolution with display size for typed signatures (prevents bold/thick rendering)
  useEffect(() => {
    if (signatureType === 'type' && canvasContainerRef.current) {
      const container = canvasContainerRef.current;
      const rect = container.getBoundingClientRect();
      const displayWidth = Math.floor(rect.width);
      const displayHeight = Math.floor(rect.height);

      if (displayWidth > 0 && displayHeight > 0 &&
          (displayWidth !== canvasSize.width || displayHeight !== canvasSize.height)) {
        setCanvasSize({ width: displayWidth, height: displayHeight });
      }
    }
  }, [signatureType, canvasSize.width, canvasSize.height]);

  // Re-render typed signature when canvas size changes
  useEffect(() => {
    if (signatureType === 'type' && typedText && canvasRef.current) {
      // Small delay to ensure canvas is resized, then re-render
      const timer = setTimeout(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Auto-size font to fit within canvas with padding
        const maxWidth = canvas.width * 0.9;
        const maxHeight = canvas.height * 0.7;
        let fontSize = Math.min(48, maxHeight * 0.8);
        let minSize = 12;
        let maxSize = Math.min(72, maxHeight);

        while (maxSize - minSize > 1) {
          fontSize = Math.floor((minSize + maxSize) / 2);
          ctx.font = `400 ${fontSize}px "${selectedFont}", cursive`;
          const metrics = ctx.measureText(typedText);
          if (metrics.width <= maxWidth && fontSize <= maxHeight * 0.8) {
            minSize = fontSize;
          } else {
            maxSize = fontSize;
          }
        }

        fontSize = minSize;
        ctx.font = `400 ${fontSize}px "${selectedFont}", cursive`;
        ctx.fillStyle = color;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(typedText, canvas.width / 2, canvas.height / 2);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [canvasSize, signatureType, typedText, selectedFont, color]);

  // Keep colorRef in sync so native touch handlers always see the current color
  useEffect(() => {
    colorRef.current = color;
  }, [color]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.strokeStyle = color;
      ctx.lineWidth = 2; // Reduced from 4 for thinner, more natural strokes
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      // Recolor existing drawing when color changes
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      // Parse the new color to RGB
      let r, g, b;
      if (color.startsWith('#')) {
        r = parseInt(color.slice(1, 3), 16);
        g = parseInt(color.slice(3, 5), 16);
        b = parseInt(color.slice(5, 7), 16);
      } else {
        // Default to black if color format is unexpected
        r = 0; g = 0; b = 0;
      }

      // Change all non-transparent pixels to the new color
      for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] > 0) { // If pixel is not fully transparent
          data[i] = r;     // Red
          data[i + 1] = g; // Green
          data[i + 2] = b; // Blue
          // Keep original alpha (data[i + 3])
        }
      }

      ctx.putImageData(imageData, 0, 0);
    }
  }, [color]);

  // Draw uploaded image (with background removed) to canvas when it changes
  useEffect(() => {
    if (uploadedImage && signatureType === 'upload' && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const img = new Image();
      img.onload = () => {
        // Calculate scaling to fit the image in canvas while maintaining aspect ratio
        const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
        const scaledWidth = img.width * scale;
        const scaledHeight = img.height * scale;
        const x = (canvas.width - scaledWidth) / 2;
        const y = (canvas.height - scaledHeight) / 2;

        ctx.drawImage(img, x, y, scaledWidth, scaledHeight);
      };
      img.src = uploadedImage;
    }
  }, [uploadedImage, signatureType]);

  const getCanvasCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();

    // Calculate the scale between canvas internal size and display size
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    // Handle both mouse and touch events
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    // Get position relative to canvas
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;

    return { x, y };
  };

  const startDrawing = (e) => {
    const { x, y } = getCanvasCoordinates(e);

    const ctx = canvasRef.current.getContext('2d');
    // Apply current color before starting new stroke
    ctx.strokeStyle = colorRef.current;
    ctx.lineWidth = 2; // Reduced from 4 for thinner, more natural strokes
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(x, y);
    isDrawingRef.current = true;
  };

  const draw = (e) => {
    if (!isDrawingRef.current) return;

    const { x, y } = getCanvasCoordinates(e);

    const ctx = canvasRef.current.getContext('2d');
    // Ensure color is applied for each stroke segment
    ctx.strokeStyle = colorRef.current;
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    isDrawingRef.current = false;
  };

  // Attach non-passive native touch listeners so preventDefault() can stop
  // page scrolling on iOS/Android while the user is drawing on the canvas.
  // React's synthetic touch events are passive by default and cannot call preventDefault().
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || signatureType !== 'draw') return;

    const handleTouchStart = (e) => {
      e.preventDefault();
      startDrawing(e);
    };

    const handleTouchMove = (e) => {
      e.preventDefault();
      draw(e);
    };

    const handleTouchEnd = () => {
      stopDrawing();
    };

    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd);

    return () => {
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
    };
  }, [signatureType]); // signatureType controls when the canvas is active for drawing

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const generateTypedSignature = (fontName = selectedFont) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (!typedText) return;

    // Auto-size font to fit within canvas with padding
    const maxWidth = canvas.width * 0.9; // 90% of canvas width
    const maxHeight = canvas.height * 0.7; // 70% of canvas height for text

    // Start with a reasonable font size and scale down if needed
    let fontSize = Math.min(48, maxHeight * 0.8); // Start smaller for initials/short text

    // Binary search for optimal font size that fits
    let minSize = 12;
    let maxSize = Math.min(72, maxHeight);

    while (maxSize - minSize > 1) {
      fontSize = Math.floor((minSize + maxSize) / 2);
      ctx.font = `400 ${fontSize}px "${fontName}", cursive`;
      const metrics = ctx.measureText(typedText);

      if (metrics.width <= maxWidth && fontSize <= maxHeight * 0.8) {
        minSize = fontSize;
      } else {
        maxSize = fontSize;
      }
    }

    // Use the found optimal size
    fontSize = minSize;
    ctx.font = `400 ${fontSize}px "${fontName}", cursive`;
    ctx.fillStyle = color;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(typedText, canvas.width / 2, canvas.height / 2);
  };

  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      image.src = url;
    });

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async (event) => {
        // Load image to get dimensions
        const img = await createImage(event.target.result);
        setImageSize({ width: img.width, height: img.height });

        // Initialize crop box centered on image
        const initialWidth = Math.min(img.width * 0.8, 400);
        const initialHeight = initialWidth / 3; // 3:1 aspect ratio for signatures
        setCropBox({
          x: (img.width - initialWidth) / 2,
          y: (img.height - initialHeight) / 2,
          width: initialWidth,
          height: initialHeight
        });

        setImageToCrop(event.target.result);
        setShowCrop(true);
      };
      reader.readAsDataURL(file);
    }
  };

  // Adaptive Background Removal for Signature Images
  // Detects background type and uses appropriate threshold
  // eslint-disable-next-line no-unused-vars
  const removeBackgroundHistogram = (sourceCanvas) => {
    const ctx = sourceCanvas.getContext('2d');
    const width = sourceCanvas.width;
    const height = sourceCanvas.height;
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;
    const pixelCount = width * height;

    // Step 1: Convert to grayscale
    const grayscale = new Uint8Array(pixelCount);

    for (let i = 0; i < pixelCount; i++) {
      const idx = i * 4;
      if (data[idx + 3] === 0) {
        grayscale[i] = 255;
        continue;
      }
      const r = data[idx], g = data[idx + 1], b = data[idx + 2];
      grayscale[i] = Math.round(r * 0.299 + g * 0.587 + b * 0.114);
    }

    // Step 2: Analyze background type
    // Sample edges and corners to determine background
    const bgSamples = [];
    const margin = Math.floor(Math.min(width, height) * 0.1);

    // Sample from corners and edges (where signature usually isn't)
    for (let y = 0; y < margin; y++) {
      for (let x = 0; x < width; x++) {
        bgSamples.push(grayscale[y * width + x]);
      }
    }
    for (let y = height - margin; y < height; y++) {
      for (let x = 0; x < width; x++) {
        bgSamples.push(grayscale[y * width + x]);
      }
    }

    bgSamples.sort((a, b) => a - b);
    const bgMedian = bgSamples[Math.floor(bgSamples.length / 2)];
    const bgBright = bgSamples[Math.floor(bgSamples.length * 0.9)]; // 90th percentile

    // Detect if there are ruled lines (rows with many similar-intensity dark pixels)
    let hasRuledLines = false;
    for (let y = 0; y < height; y++) {
      let darkCount = 0;
      let darkSum = 0;
      for (let x = 0; x < width; x++) {
        const g = grayscale[y * width + x];
        if (g < 150 && g < bgMedian - 20) {
          darkCount++;
          darkSum += g;
        }
      }
      // If >30% of row is dark with consistent intensity, likely a ruled line
      if (darkCount > width * 0.3) {
        const avgDark = darkSum / darkCount;
        if (avgDark > 60 && avgDark < 140) { // Ruled lines are typically gray, not black
          hasRuledLines = true;
          break;
        }
      }
    }

    // Detect gray background (significant portion of mid-gray pixels)
    let grayPixelCount = 0;
    for (let i = 0; i < pixelCount; i++) {
      if (grayscale[i] >= 80 && grayscale[i] <= 180) {
        grayPixelCount++;
      }
    }
    const hasGrayBackground = grayPixelCount > pixelCount * 0.2;

    const isCleanWhiteBackground = bgBright > 200 && !hasRuledLines && !hasGrayBackground;

    console.log(`Background: median=${bgMedian}, bright=${bgBright}, ruledLines=${hasRuledLines}, grayBg=${hasGrayBackground}, cleanWhite=${isCleanWhiteBackground}`);

    // Step 3: Calculate threshold based on background type
    const sortedGrays = Array.from(grayscale).sort((a, b) => a - b);
    const darkest5Percent = sortedGrays[Math.floor(pixelCount * 0.05)];
    const darkest10Percent = sortedGrays[Math.floor(pixelCount * 0.10)];
    const darkest15Percent = sortedGrays[Math.floor(pixelCount * 0.15)];

    let inkThreshold;

    if (isCleanWhiteBackground) {
      // Clean white paper - use relaxed threshold to keep full signature
      inkThreshold = Math.min(darkest15Percent + 40, bgMedian - 30);
      inkThreshold = Math.max(inkThreshold, 120); // Allow lighter strokes
      console.log('Using RELAXED threshold for clean background');
    } else if (hasRuledLines || hasGrayBackground) {
      // Complex background - use tight threshold
      inkThreshold = Math.min(darkest5Percent + 25, 80);
      console.log('Using TIGHT threshold for complex background');
    } else {
      // Medium case
      inkThreshold = Math.min(darkest10Percent + 30, 100);
      console.log('Using MEDIUM threshold');
    }

    console.log(`Ink threshold: ${inkThreshold}`);

    // Step 4: Create ink mask
    const inkMask = new Uint8Array(pixelCount);

    for (let i = 0; i < pixelCount; i++) {
      if (grayscale[i] < inkThreshold) {
        inkMask[i] = 1;
      }
    }

    // Step 5: Remove horizontal lines (only if detected)
    const lineFiltered = new Uint8Array(pixelCount);

    if (hasRuledLines) {
      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          const idx = y * width + x;

          if (inkMask[idx] === 0) {
            lineFiltered[idx] = 0;
            continue;
          }

          // Count horizontal run
          let runLength = 1;
          let lx = x - 1;
          while (lx >= 0 && inkMask[y * width + lx]) { runLength++; lx--; }
          let rx = x + 1;
          while (rx < width && inkMask[y * width + rx]) { runLength++; rx++; }

          // Check vertical neighbors
          let hasVertical = false;
          for (let dx = -2; dx <= 2; dx++) {
            const checkX = x + dx;
            if (checkX < 0 || checkX >= width) continue;
            if ((y > 0 && inkMask[(y-1) * width + checkX]) ||
                (y < height-1 && inkMask[(y+1) * width + checkX])) {
              hasVertical = true;
              break;
            }
          }

          // Long horizontal without vertical = line
          if (runLength > width * 0.08 && !hasVertical) {
            lineFiltered[idx] = 0;
          } else {
            lineFiltered[idx] = 1;
          }
        }
      }
    } else {
      // No ruled lines - keep all ink
      for (let i = 0; i < pixelCount; i++) {
        lineFiltered[i] = inkMask[i];
      }
    }

    // Step 6: Remove isolated noise
    const cleanedMask = new Uint8Array(pixelCount);

    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const idx = y * width + x;
        if (lineFiltered[idx] === 0) continue;

        let neighbors = 0;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            if (dx === 0 && dy === 0) continue;
            if (lineFiltered[(y + dy) * width + (x + dx)]) neighbors++;
          }
        }

        cleanedMask[idx] = neighbors >= 1 ? 1 : 0;
      }
    }

    // Step 7: Create alpha with smooth edges
    const alphaMask = new Uint8Array(pixelCount);
    const transitionZone = 15; // Wider transition for cleaner edges

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const idx = y * width + x;
        const gray = grayscale[idx];

        if (cleanedMask[idx]) {
          alphaMask[idx] = 255;
        } else if (x > 0 && x < width-1 && y > 0 && y < height-1) {
          // Soft edges near ink
          let nearInk = false;
          for (let dy = -1; dy <= 1; dy++) {
            for (let dx = -1; dx <= 1; dx++) {
              if (cleanedMask[(y + dy) * width + (x + dx)]) {
                nearInk = true;
                break;
              }
            }
            if (nearInk) break;
          }

          if (nearInk && gray < inkThreshold + transitionZone) {
            // Gradient based on how close to threshold
            const t = (inkThreshold + transitionZone - gray) / transitionZone;
            alphaMask[idx] = Math.round(200 * t);
          }
        }
      }
    }

    // Step 8: Apply mask (keep original colors)
    for (let i = 0; i < pixelCount; i++) {
      const idx = i * 4;
      data[idx + 3] = alphaMask[i];
    }

    ctx.putImageData(imageData, 0, 0);
    return sourceCanvas.toDataURL('image/png');
  };

  // Calculate the actual displayed image dimensions and offset within the container
  // This accounts for object-contain which may leave empty space on sides or top/bottom
  const getDisplayedImageInfo = useCallback(() => {
    const container = cropContainerRef.current;
    if (!container || !imageSize.width || !imageSize.height) {
      return { scale: 1, offsetX: 0, offsetY: 0 };
    }

    const containerWidth = container.offsetWidth;
    const containerHeight = container.offsetHeight;
    const imageAspect = imageSize.width / imageSize.height;
    const containerAspect = containerWidth / containerHeight;

    let displayedWidth, displayedHeight, offsetX, offsetY;

    if (imageAspect > containerAspect) {
      // Image is wider - it fills the width, has empty space top/bottom
      displayedWidth = containerWidth;
      displayedHeight = containerWidth / imageAspect;
      offsetX = 0;
      offsetY = (containerHeight - displayedHeight) / 2;
    } else {
      // Image is taller - it fills the height, has empty space left/right
      displayedHeight = containerHeight;
      displayedWidth = containerHeight * imageAspect;
      offsetX = (containerWidth - displayedWidth) / 2;
      offsetY = 0;
    }

    const scale = displayedWidth / imageSize.width;
    return { scale, offsetX, offsetY, displayedWidth, displayedHeight };
  }, [imageSize]);

  // Update display info when crop modal is shown or container size changes
  useEffect(() => {
    if (!showCrop || !cropContainerRef.current) return;

    const updateDisplayInfo = () => {
      const info = getDisplayedImageInfo();
      setDisplayInfo(info);
    };

    // Initial update after a small delay to ensure container is rendered
    const timer = setTimeout(updateDisplayInfo, 50);

    // Also update on window resize
    window.addEventListener('resize', updateDisplayInfo);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateDisplayInfo);
    };
  }, [showCrop, imageSize, getDisplayedImageInfo]);

  // Handle mouse down on crop box (start drag or resize)
  const handleCropMouseDown = (e, action) => {
    e.preventDefault();
    e.stopPropagation();

    const container = cropContainerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    setDragStart({ x, y, boxX: cropBox.x, boxY: cropBox.y, boxW: cropBox.width, boxH: cropBox.height });

    if (action === 'move') {
      setIsDragging(true);
    } else {
      setIsResizing(action);
    }
  };

  // Handle mouse move for dragging/resizing
  const handleCropMouseMove = useCallback((e) => {
    if (!isDragging && !isResizing) return;

    const container = cropContainerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    const dx = x - dragStart.x;
    const dy = y - dragStart.y;

    // Get the actual displayed image info (accounting for object-contain)
    const { scale } = getDisplayedImageInfo();
    const maxX = imageSize.width;
    const maxY = imageSize.height;

    if (isDragging) {
      // Move the crop box
      let newX = dragStart.boxX + dx / scale;
      let newY = dragStart.boxY + dy / scale;

      // Clamp to image bounds
      newX = Math.max(0, Math.min(newX, maxX - cropBox.width));
      newY = Math.max(0, Math.min(newY, maxY - cropBox.height));

      setCropBox(prev => ({ ...prev, x: newX, y: newY }));
    } else if (isResizing) {
      // Resize the crop box
      let newBox = { ...cropBox };
      const minSize = 50;

      switch (isResizing) {
        case 'se':
          newBox.width = Math.max(minSize, Math.min(dragStart.boxW + dx / scale, maxX - cropBox.x));
          newBox.height = Math.max(minSize, Math.min(dragStart.boxH + dy / scale, maxY - cropBox.y));
          break;
        case 'sw':
          const newWidthSW = Math.max(minSize, dragStart.boxW - dx / scale);
          newBox.x = dragStart.boxX + (dragStart.boxW - newWidthSW);
          newBox.width = newWidthSW;
          newBox.height = Math.max(minSize, Math.min(dragStart.boxH + dy / scale, maxY - cropBox.y));
          if (newBox.x < 0) { newBox.width += newBox.x; newBox.x = 0; }
          break;
        case 'ne':
          newBox.width = Math.max(minSize, Math.min(dragStart.boxW + dx / scale, maxX - cropBox.x));
          const newHeightNE = Math.max(minSize, dragStart.boxH - dy / scale);
          newBox.y = dragStart.boxY + (dragStart.boxH - newHeightNE);
          newBox.height = newHeightNE;
          if (newBox.y < 0) { newBox.height += newBox.y; newBox.y = 0; }
          break;
        case 'nw':
          const newWidthNW = Math.max(minSize, dragStart.boxW - dx / scale);
          const newHeightNW = Math.max(minSize, dragStart.boxH - dy / scale);
          newBox.x = dragStart.boxX + (dragStart.boxW - newWidthNW);
          newBox.y = dragStart.boxY + (dragStart.boxH - newHeightNW);
          newBox.width = newWidthNW;
          newBox.height = newHeightNW;
          if (newBox.x < 0) { newBox.width += newBox.x; newBox.x = 0; }
          if (newBox.y < 0) { newBox.height += newBox.y; newBox.y = 0; }
          break;
        case 'n':
          const newHeightN = Math.max(minSize, dragStart.boxH - dy / scale);
          newBox.y = dragStart.boxY + (dragStart.boxH - newHeightN);
          newBox.height = newHeightN;
          if (newBox.y < 0) { newBox.height += newBox.y; newBox.y = 0; }
          break;
        case 's':
          newBox.height = Math.max(minSize, Math.min(dragStart.boxH + dy / scale, maxY - cropBox.y));
          break;
        case 'e':
          newBox.width = Math.max(minSize, Math.min(dragStart.boxW + dx / scale, maxX - cropBox.x));
          break;
        case 'w':
          const newWidthW = Math.max(minSize, dragStart.boxW - dx / scale);
          newBox.x = dragStart.boxX + (dragStart.boxW - newWidthW);
          newBox.width = newWidthW;
          if (newBox.x < 0) { newBox.width += newBox.x; newBox.x = 0; }
          break;
        default:
          break;
      }

      setCropBox(newBox);
    }
  }, [isDragging, isResizing, dragStart, cropBox, imageSize, getDisplayedImageInfo]);

  // Handle mouse up
  const handleCropMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(null);
  }, []);

  // Add global mouse and touch listeners when dragging/resizing
  useEffect(() => {
    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleCropMouseMove);
      document.addEventListener('mouseup', handleCropMouseUp);
      document.addEventListener('touchmove', handleCropMouseMove, { passive: false });
      document.addEventListener('touchend', handleCropMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleCropMouseMove);
        document.removeEventListener('mouseup', handleCropMouseUp);
        document.removeEventListener('touchmove', handleCropMouseMove);
        document.removeEventListener('touchend', handleCropMouseUp);
      };
    }
  }, [isDragging, isResizing, handleCropMouseMove, handleCropMouseUp]);

  const applyCrop = async () => {
    if (!imageToCrop) return;

    const image = await createImage(imageToCrop);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    // Use the crop box dimensions
    canvas.width = cropBox.width;
    canvas.height = cropBox.height;

    // Fill with white first so we have a consistent background for processing
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw the cropped region
    ctx.drawImage(
      image,
      cropBox.x, cropBox.y, cropBox.width, cropBox.height,
      0, 0, cropBox.width, cropBox.height
    );

    // Get cropped image data URL
    const croppedDataUrl = canvas.toDataURL('image/png');

    // Draw cropped image on the main canvas
    const img = await createImage(croppedDataUrl);
    const mainCanvas = canvasRef.current;
    const mainCtx = mainCanvas.getContext('2d');

    // Fill main canvas with white first (for consistent background removal)
    mainCtx.fillStyle = '#FFFFFF';
    mainCtx.fillRect(0, 0, mainCanvas.width, mainCanvas.height);

    // Scale image to fit canvas while maintaining aspect ratio
    const scale = Math.min(mainCanvas.width / img.width, mainCanvas.height / img.height);
    const scaledWidth = img.width * scale;
    const scaledHeight = img.height * scale;
    const x = (mainCanvas.width - scaledWidth) / 2;
    const y = (mainCanvas.height - scaledHeight) / 2;

    mainCtx.drawImage(img, x, y, scaledWidth, scaledHeight);

    // Apply background removal - handles any paper color
    const processedImage = removeWhiteBackground(mainCanvas);

    setUploadedImage(processedImage);
    setShowCrop(false);
    setImageToCrop(null);
  };

  // Background removal for uploaded signatures
  // Works with ANY paper color (white, beige, grey, lined paper, etc.)
  // Uses histogram-based approach to find the dominant background color
  const removeWhiteBackground = (sourceCanvas) => {
    const ctx = sourceCanvas.getContext('2d');
    const width = sourceCanvas.width;
    const height = sourceCanvas.height;
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;

    // Step 1: Build histogram of all pixel brightness values
    const histogram = new Array(256).fill(0);
    const allBrightness = [];

    for (let i = 0; i < data.length; i += 4) {
      if (data[i+3] > 128) {
        const brightness = Math.round((data[i] + data[i+1] + data[i+2]) / 3);
        histogram[brightness]++;
        allBrightness.push(brightness);
      }
    }

    if (allBrightness.length === 0) {
      console.log('No content pixels found');
      return sourceCanvas.toDataURL('image/png');
    }

    // Step 2: Find the dominant brightness (background) using histogram peak
    // The background is typically the most common brightness value
    let maxCount = 0;
    let dominantBrightness = 128;

    // Use a sliding window to find the peak (smoothed)
    for (let i = 10; i < 246; i++) {
      const windowSum = histogram[i-2] + histogram[i-1] + histogram[i] + histogram[i+1] + histogram[i+2];
      if (windowSum > maxCount) {
        maxCount = windowSum;
        dominantBrightness = i;
      }
    }

    // Step 3: Get percentiles for threshold calculation
    allBrightness.sort((a, b) => a - b);
    const p5 = allBrightness[Math.floor(allBrightness.length * 0.05)];
    const p10 = allBrightness[Math.floor(allBrightness.length * 0.10)];
    const p90 = allBrightness[Math.floor(allBrightness.length * 0.90)];
    const minBrightness = allBrightness[0];

    // The background brightness is the higher of dominant peak or 90th percentile
    const bgBrightness = Math.max(dominantBrightness, p90);

    // Step 4: Calculate ink threshold
    // Ink is the dark content - typically in the bottom 5-15% of brightness
    // The threshold should be set to capture ink but reject ALL background variations

    const brightnessRange = bgBrightness - minBrightness;
    let inkThreshold;

    // Key insight: ink is usually very dark (< 100 brightness typically)
    // Background can vary but is usually > 150
    // We set threshold based on where the "ink cluster" ends

    if (brightnessRange > 120) {
      // Very good contrast - ink threshold at 25% of range from bottom
      inkThreshold = minBrightness + brightnessRange * 0.25;
    } else if (brightnessRange > 80) {
      // Good contrast - 30%
      inkThreshold = minBrightness + brightnessRange * 0.30;
    } else if (brightnessRange > 50) {
      // Medium contrast - 40%
      inkThreshold = minBrightness + brightnessRange * 0.40;
    } else {
      // Low contrast - 50%
      inkThreshold = minBrightness + brightnessRange * 0.50;
    }

    // Use percentile as alternative and take the lower (more aggressive for bg removal)
    const percentileThreshold = p10 + (p10 - minBrightness) * 0.5;

    // Take the LOWER threshold to be more aggressive in removing background
    inkThreshold = Math.min(inkThreshold, percentileThreshold + 20);

    // But don't go below a reasonable minimum to keep the ink
    inkThreshold = Math.max(inkThreshold, p5 + 5);

    // Hard cap: ink threshold should be well below background
    inkThreshold = Math.min(inkThreshold, bgBrightness - 50);

    // Absolute bounds
    inkThreshold = Math.max(25, Math.min(inkThreshold, 150));

    console.log(`Background removal: dominant=${dominantBrightness}, bg=${bgBrightness}, min=${minBrightness}, p5=${p5}, p10=${p10}, range=${brightnessRange}, threshold=${inkThreshold.toFixed(0)}`);

    // Step 5: Apply BINARY threshold - no partial transparency
    for (let i = 0; i < data.length; i += 4) {
      if (data[i+3] < 128) {
        data[i+3] = 0;
        continue;
      }

      const brightness = (data[i] + data[i+1] + data[i+2]) / 3;

      if (brightness <= inkThreshold) {
        // This is ink - keep it fully opaque and darken slightly
        data[i] = Math.max(0, Math.round(data[i] * 0.85));
        data[i+1] = Math.max(0, Math.round(data[i+1] * 0.85));
        data[i+2] = Math.max(0, Math.round(data[i+2] * 0.85));
        data[i+3] = 255;
      } else {
        // This is background - make it fully transparent
        data[i+3] = 0;
      }
    }

    ctx.putImageData(imageData, 0, 0);
    console.log('Background removal complete');
    return sourceCanvas.toDataURL('image/png');
  };

  const cancelCrop = () => {
    setShowCrop(false);
    setImageToCrop(null);
    setCropBox({ x: 50, y: 50, width: 300, height: 100 });
  };

  // Crop canvas to content bounds to ensure signatures fill the field properly
  // Works for both transparent backgrounds (drawn/typed) and white backgrounds (uploaded)
  const cropCanvasToContent = (sourceCanvas) => {
    const ctx = sourceCanvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, sourceCanvas.width, sourceCanvas.height);
    const data = imageData.data;
    const width = sourceCanvas.width;
    const height = sourceCanvas.height;

    // Detect if canvas has transparent background or white background
    // Check corners to determine background type
    let hasTransparentBg = false;
    const cornerPixels = [
      0, // top-left
      (width - 1) * 4, // top-right
      (height - 1) * width * 4, // bottom-left
      ((height - 1) * width + (width - 1)) * 4 // bottom-right
    ];
    for (const idx of cornerPixels) {
      if (data[idx + 3] < 128) { // Alpha less than 50%
        hasTransparentBg = true;
        break;
      }
    }

    // Find bounding box of content pixels
    let minX = width;
    let minY = height;
    let maxX = 0;
    let maxY = 0;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const idx = (y * width + x) * 4;
        const r = data[idx], g = data[idx + 1], b = data[idx + 2], alpha = data[idx + 3];

        let isContent = false;
        if (hasTransparentBg) {
          // For transparent background, any non-transparent pixel is content
          isContent = alpha > 10;
        } else {
          // For white/opaque background, dark pixels are content
          // Using threshold of 245 for near-white detection
          const isNearWhite = r > 245 && g > 245 && b > 245;
          isContent = !isNearWhite && alpha > 10;
        }

        if (isContent) {
          minX = Math.min(minX, x);
          minY = Math.min(minY, y);
          maxX = Math.max(maxX, x);
          maxY = Math.max(maxY, y);
        }
      }
    }

    // If no content found, return original canvas
    if (minX > maxX || minY > maxY) {
      console.log('No content found, returning original canvas');
      return sourceCanvas.toDataURL('image/png');
    }

    // Add small padding (5% of content size, minimum 5px)
    const contentWidth = maxX - minX;
    const contentHeight = maxY - minY;
    const padX = Math.max(5, Math.floor(contentWidth * 0.05));
    const padY = Math.max(5, Math.floor(contentHeight * 0.05));

    // Calculate crop bounds with padding
    const cropX = Math.max(0, minX - padX);
    const cropY = Math.max(0, minY - padY);
    const cropWidth = Math.min(width - cropX, contentWidth + padX * 2);
    const cropHeight = Math.min(height - cropY, contentHeight + padY * 2);

    // Create cropped canvas
    const croppedCanvas = document.createElement('canvas');
    croppedCanvas.width = cropWidth;
    croppedCanvas.height = cropHeight;
    const croppedCtx = croppedCanvas.getContext('2d');

    croppedCtx.drawImage(
      sourceCanvas,
      cropX, cropY, cropWidth, cropHeight,
      0, 0, cropWidth, cropHeight
    );

    console.log(`Cropped signature: ${width}x${height} -> ${cropWidth}x${cropHeight}, bg=${hasTransparentBg ? 'transparent' : 'white'}`);

    return croppedCanvas.toDataURL('image/png');
  };

  const saveSignature = () => {
    const canvas = canvasRef.current;
    let dataUrl;

    if (signatureType === 'upload') {
      // For uploaded images, the user already cropped manually.
      // Don't auto-crop again to preserve the exact image they selected.
      dataUrl = canvas.toDataURL('image/png');
      console.log('Saving uploaded signature as-is (no auto-crop)');
    } else {
      // For drawn/typed signatures, crop transparent padding to fill field properly
      dataUrl = cropCanvasToContent(canvas);
    }

    onSave(dataUrl, signatureType);
  };

  return (
    <div className="space-y-4" data-testid="signature-canvas">
      <Tabs value={signatureType} onValueChange={setSignatureType}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="draw" data-testid="tab-draw">
            <Pen size={16} className="mr-2" />
            Draw
          </TabsTrigger>
          <TabsTrigger value="type" data-testid="tab-type">
            <Type size={16} className="mr-2" />
            Type
          </TabsTrigger>
          <TabsTrigger value="upload" data-testid="tab-upload">
            <Upload size={16} className="mr-2" />
            Upload
          </TabsTrigger>
        </TabsList>

        <TabsContent value="draw" className="space-y-4">
          <div className="border-2 border-gray-300 rounded-lg bg-white">
            <canvas
              ref={canvasRef}
              width={600}
              height={200}
              className="w-full cursor-crosshair touch-none"
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              // Touch events handled by native listeners in useEffect above (non-passive)
              data-testid="draw-canvas"
            />
          </div>
          <Button
            variant="outline"
            onClick={clearCanvas}
            className="w-full"
            data-testid="clear-canvas-btn"
          >
            <Eraser size={16} className="mr-2" />
            Clear
          </Button>
        </TabsContent>

        <TabsContent value="type" className="space-y-4">
          <Input
            type="text"
            placeholder="Type your name"
            value={typedText}
            onChange={(e) => {
              setTypedText(e.target.value);
            }}
            onKeyUp={() => generateTypedSignature(selectedFont)}
            className="text-lg"
            data-testid="type-signature-input"
          />
          
          {/* Signature Style Previews */}
          <div className="grid grid-cols-2 gap-4">
            {[
              { name: 'Dancing Script', style: 'normal' },
              { name: 'Pacifico', style: 'normal' },
              { name: 'Great Vibes', style: 'normal' },
              { name: 'Allura', style: 'normal' }
            ].map((fontOption) => (
              <button
                key={fontOption.name}
                onClick={() => {
                  setSelectedFont(fontOption.name);
                  generateTypedSignature(fontOption.name);
                }}
                className={`p-4 rounded-lg border-2 transition-all bg-white hover:shadow-md ${
                  selectedFont === fontOption.name
                    ? 'border-indigo-600 ring-2 ring-indigo-200'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <div
                  className="text-3xl text-center"
                  style={{
                    fontFamily: fontOption.name,
                    color: color,
                    fontWeight: 400
                  }}
                >
                  {typedText || 'Your Name'}
                </div>
              </button>
            ))}
          </div>
          
          <div
            ref={canvasContainerRef}
            className="border-2 border-gray-300 rounded-lg bg-white h-[200px]"
          >
            <canvas
              ref={canvasRef}
              width={canvasSize.width}
              height={canvasSize.height}
              style={{ width: '100%', height: '100%' }}
            />
          </div>
        </TabsContent>

        <TabsContent value="upload" className="space-y-4">
          <Input
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            data-testid="upload-signature-input"
          />
          <div className="border-2 border-gray-300 rounded-lg bg-white h-[200px]">
            <canvas
              ref={canvasRef}
              width={600}
              height={200}
              className="w-full"
            />
          </div>
        </TabsContent>
      </Tabs>

      <div className="flex gap-4">
        <Button
          onClick={saveSignature}
          className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600"
          data-testid="save-signature-btn"
        >
          Save Signature
        </Button>
        <Button
          variant="outline"
          onClick={onCancel}
          className="flex-1"
          data-testid="cancel-signature-btn"
        >
          Cancel
        </Button>
      </div>

      {/* Crop Modal - Drag to Resize */}
      {showCrop && imageToCrop && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-3xl mx-4">
            <h3 className="text-lg font-semibold mb-2 flex items-center">
              <Crop className="mr-2" size={20} />
              Crop Your Signature
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Drag the corners or edges to resize the crop area.
            </p>

            {/* Crop Container - transparent checkerboard background */}
            <div
              ref={cropContainerRef}
              className="relative w-full rounded-lg overflow-hidden border border-gray-300"
              style={{
                height: Math.min(400, (imageSize.height / imageSize.width) * 600) || 300,
                background: 'repeating-conic-gradient(#f0f0f0 0% 25%, #fff 0% 50%) 50% / 20px 20px'
              }}
            >
              {/* Image displayed at actual size, centered */}
              <img
                src={imageToCrop}
                alt="To crop"
                className="w-full h-full object-contain"
                draggable={false}
              />

              {/* Crop Box - positioned correctly accounting for object-contain */}
              <div
                className="absolute border-3 border-indigo-600"
                style={{
                  left: `${displayInfo.offsetX + (cropBox.x * displayInfo.scale)}px`,
                  top: `${displayInfo.offsetY + (cropBox.y * displayInfo.scale)}px`,
                  width: `${cropBox.width * displayInfo.scale}px`,
                  height: `${cropBox.height * displayInfo.scale}px`,
                  cursor: isDragging ? 'grabbing' : 'grab',
                  borderWidth: '3px',
                  backgroundColor: 'transparent'
                }}
                onMouseDown={(e) => handleCropMouseDown(e, 'move')}
                onTouchStart={(e) => handleCropMouseDown(e, 'move')}
              >
                {/* Light grid lines inside crop area */}
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute left-1/3 top-0 bottom-0 w-px bg-indigo-400 opacity-40"></div>
                  <div className="absolute left-2/3 top-0 bottom-0 w-px bg-indigo-400 opacity-40"></div>
                  <div className="absolute top-1/3 left-0 right-0 h-px bg-indigo-400 opacity-40"></div>
                  <div className="absolute top-2/3 left-0 right-0 h-px bg-indigo-400 opacity-40"></div>
                </div>

                {/* Resize handles - Corners */}
                <div
                  className="absolute -left-3 -top-3 w-6 h-6 bg-indigo-600 border-2 border-white rounded-sm cursor-nw-resize shadow-md"
                  onMouseDown={(e) => handleCropMouseDown(e, 'nw')}
                  onTouchStart={(e) => handleCropMouseDown(e, 'nw')}
                />
                <div
                  className="absolute -right-3 -top-3 w-6 h-6 bg-indigo-600 border-2 border-white rounded-sm cursor-ne-resize shadow-md"
                  onMouseDown={(e) => handleCropMouseDown(e, 'ne')}
                  onTouchStart={(e) => handleCropMouseDown(e, 'ne')}
                />
                <div
                  className="absolute -left-3 -bottom-3 w-6 h-6 bg-indigo-600 border-2 border-white rounded-sm cursor-sw-resize shadow-md"
                  onMouseDown={(e) => handleCropMouseDown(e, 'sw')}
                  onTouchStart={(e) => handleCropMouseDown(e, 'sw')}
                />
                <div
                  className="absolute -right-3 -bottom-3 w-6 h-6 bg-indigo-600 border-2 border-white rounded-sm cursor-se-resize shadow-md"
                  onMouseDown={(e) => handleCropMouseDown(e, 'se')}
                  onTouchStart={(e) => handleCropMouseDown(e, 'se')}
                />

                {/* Resize handles - Edges */}
                <div
                  className="absolute left-1/2 -translate-x-1/2 -top-3 w-10 h-6 bg-indigo-600 border-2 border-white rounded-sm cursor-n-resize shadow-md"
                  onMouseDown={(e) => handleCropMouseDown(e, 'n')}
                  onTouchStart={(e) => handleCropMouseDown(e, 'n')}
                />
                <div
                  className="absolute left-1/2 -translate-x-1/2 -bottom-3 w-10 h-6 bg-indigo-600 border-2 border-white rounded-sm cursor-s-resize shadow-md"
                  onMouseDown={(e) => handleCropMouseDown(e, 's')}
                  onTouchStart={(e) => handleCropMouseDown(e, 's')}
                />
                <div
                  className="absolute top-1/2 -translate-y-1/2 -left-3 w-6 h-10 bg-indigo-600 border-2 border-white rounded-sm cursor-w-resize shadow-md"
                  onMouseDown={(e) => handleCropMouseDown(e, 'w')}
                  onTouchStart={(e) => handleCropMouseDown(e, 'w')}
                />
                <div
                  className="absolute top-1/2 -translate-y-1/2 -right-3 w-6 h-10 bg-indigo-600 border-2 border-white rounded-sm cursor-e-resize shadow-md"
                  onMouseDown={(e) => handleCropMouseDown(e, 'e')}
                  onTouchStart={(e) => handleCropMouseDown(e, 'e')}
                />
              </div>
            </div>

            {/* Crop dimensions display */}
            <div className="mt-3 text-sm text-gray-500 text-center">
              Crop size: {Math.round(cropBox.width)} × {Math.round(cropBox.height)} pixels
            </div>

            <div className="flex gap-4 mt-4">
              <Button
                onClick={applyCrop}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700"
              >
                Apply Crop
              </Button>
              <Button
                variant="outline"
                onClick={cancelCrop}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignatureCanvas;
